import React, { useEffect, useState, useMemo } from 'react';
import { useThemeContext } from '../contexts/ThemeContext';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import KPICard from '../components/dashboard/KPICard';
import { Select } from '../components/ui';
import { Loading } from '../components/utils';
import { useDashboard } from '../hooks/useDashboard';
import { useAppContext } from '../contexts/Storage';
import { useCurrency } from '../utils/globalCurrency';
import emptyFolderIcon from '../assets/images/empty_folder.png';

const DashboardPage: React.FC = () => {
  const { isDark } = useThemeContext();
  const { setNavLinks } = useNavLinksContext();
  const { user } = useAppContext();
  const { kpis, isLoading, fetchDashboardKpis } = useDashboard();
  const { format, formatWhole } = useCurrency();
  const [selectedRecord, setSelectedRecord] = useState<string>('all');

  // Set navigation links for the Dashboard page
  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/records', label: 'Records' },
      ],
    });

    // Cleanup is handled by the NavLinksProvider default
  }, [setNavLinks]);

  // Fetch dashboard KPIs on component mount
  useEffect(() => {
    if (user?.id) {
      fetchDashboardKpis();
    }
  }, [fetchDashboardKpis, user?.id]);

  // Extract unique records from KPIs
  const recordOptions = useMemo(() => {
    if (!kpis || kpis.length === 0) {
      return [
        { value: 'all', label: 'All Records' },
      ];
    }

    // Get unique records from KPIs
    const uniqueRecords = new Map<number, { id: number, name: string }>();

    kpis.forEach(kpi => {
      if (kpi.record_id && kpi.record_name) {
        if (!uniqueRecords.has(kpi.record_id)) {
          uniqueRecords.set(kpi.record_id, {
            id: kpi.record_id,
            name: kpi.record_name
          });
        }
      }
    });

    // Convert to dropdown options
    const recordOptionsArray = Array.from(uniqueRecords.values())
      .map(record => ({
        value: record.id.toString(),
        label: record.name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [
      { value: 'all', label: 'All Records' },
      ...recordOptionsArray
    ];
  }, [kpis]);

  // Filter KPIs based on selected record
  const filteredKpis = useMemo(() => {
    if (!kpis || kpis.length === 0) return [];

    if (selectedRecord === 'all') {
      return kpis;
    }

    return kpis.filter(kpi => kpi.record_id?.toString() === selectedRecord);
  }, [kpis, selectedRecord]);

  // Calculate totals from filtered KPIs
  const calculateTotal = (type: 'capital' | 'assets' | 'liabilities') => {
    if (!filteredKpis || filteredKpis.length === 0) return 0;

    switch (type) {
      case 'capital':
        // Sum of all balance amounts where balance_type is CREDIT
        return filteredKpis
          .filter(kpi => kpi.balance_type === 'CREDIT')
          .reduce((sum, kpi) => sum + (kpi.balance_amount || 0), 0);
      case 'assets':
        // Sum of all balance amounts where transaction_entity_type is 'asset'
        return filteredKpis
          .filter(kpi => kpi.transaction_entity_type === 'asset')
          .reduce((sum, kpi) => sum + (kpi.balance_amount || 0), 0);
      case 'liabilities':
        // Sum of all balance amounts where transaction_entity_type is 'liability'
        return filteredKpis
          .filter(kpi => kpi.transaction_entity_type === 'liability')
          .reduce((sum, kpi) => sum + (kpi.balance_amount || 0), 0);
      default:
        return 0;
    }
  };

  

    // Get KPIs for cards (top 5 or all if less than 5)
  const getKpiCards = () => {
    if (!filteredKpis || filteredKpis.length === 0) return [];

    return filteredKpis.slice(0, 5).map((kpi, index) => {
      // For each KPI, we need to format with its specific currency
      // Since format uses selected record's currency, we need a custom formatter for KPIs
      const currencySymbol = kpi.record_currency_code || '$';
      const amount = kpi.balance_amount || 0;
      const formattedNumber = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
      
      return {
        title: kpi.transaction_entity_name || 'Unknown',
        value: `${currencySymbol}${formattedNumber}`,
        change: kpi.difference_percentage || 0,
        isChangePositive: kpi.difference_type === 'POSITIVE',
        recordName: kpi.record_name || 'Record',
        currencyCode: kpi.record_currency_code || 'USD',
      };
    });
  };

  const kpiCards = getKpiCards();
  const capitalTotal = calculateTotal('capital');
  const assetsTotal = calculateTotal('assets');
  const liabilitiesTotal = calculateTotal('liabilities');

  

  const handleRecordChange = (value: string) => {
    setSelectedRecord(value);
  };

  return (
    <div className={`h-shink-from-navbar h-fill-from-navbar lg:px-2 bg-bg_grey_light text-dark overflow-y-scroll`}>
      <div className="max-w-7xl mx-auto">

        {/* Header and title */}
        <div className="p-6 pb-3 bg-light rounded-t-lg mb-4 flex justify-between items-center">
          <h3 className="text-xl mx-4">Overview</h3>
          {/* Dropdown to select record */}
          <div className="relative">
            <Select
              options={recordOptions}
              value={selectedRecord}
              onChange={handleRecordChange}
              placeholder="Select record"
              className="w-48"
              disabled={isLoading || kpis.length === 0}
            />
          </div>
        </div>

        {isLoading && (!kpis || kpis.length === 0) ? (
          <Loading width="30px" height="30px" className="mx-2 mb-4" />
        ) : (
          <>
            {/* Balances */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-4 text-center">
              {/* Overview Cards */}
              <div className={`p-4 mx-8 md:mx-2 lg:mx-0 rounded-lg bg-light border border-secondary`}>
                <h3 className="text-md mb-2">Capital</h3>
                <hr className="my-2 border-secondary" />
                <p
                  className="text-lg font-meduim text-dark dashboard-figure"
                        title={formatWhole(capitalTotal)}
    >
      {formatWhole(capitalTotal)}
                </p>
              </div>

              <div className={`p-4 mx-8 md:mx-2 lg:mx-0 rounded-lg bg-light border border-primary`}>
                <h3 className="text-md mb-2">Assets</h3>
                <hr className="my-2 border-primary" />
                <p
                  className="text-lg font-meduim text-dark dashboard-figure"
                        title={formatWhole(assetsTotal)}
    >
      {formatWhole(assetsTotal)}
                </p>
              </div>

              <div className={`p-4 mx-8 md:mx-2 lg:mx-0 rounded-lg bg-light border border-secondary`}>
                <h3 className="text-md mb-2">Liabilities</h3>
                <hr className="my-2 border-secondary" />
                <p
                  className="text-lg font-meduim text-dark dashboard-figure"
                                      title={formatWhole(liabilitiesTotal)}
                  >
                    {formatWhole(liabilitiesTotal)}
                </p>
              </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-4 lg:me-1">
              {kpiCards.length > 0 ? (
                kpiCards.map((kpi, index) => (
                  <KPICard
                    key={index}
                    props={{
                      title: kpi.title,
                      value: kpi.value,
                      change: kpi.change,
                      isChangePositive: kpi.isChangePositive,
                      recordName: kpi.recordName
                    }}
                  />
                ))
              ) : (
                <div className="col-span-2 text-center py-8">
                  <img
                    src={emptyFolderIcon}
                    alt="No data"
                    className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 opacity-50"
                  />
                  <p className="text-grey_dark">
                    {selectedRecord === 'all'
                      ? 'No KPI data available for any records'
                      : 'No KPI data available for the selected record'}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;